/**
 * PR-35A — Poll & Prediction lifecycle constants + helpers.
 * Auto-dismiss policy, normalization, deduplication and accuracy stats are
 * all kept in a single deterministic module. No generative AI is used.
 */
import { trackEvent } from "@/lib/audit";

export interface PollLifecycleConfig {
  countdownSec: number;
  postVoteSec: number;
  celebrationSec: number;
  pointsOnCorrect: number;
  confettiEnabled: boolean;
}

const STORAGE_KEY = "vs:pr35a:lifecycle";
const DEDUP_KEY = "vs:pr35a:dedup";
const STATS_KEY = "vs:pr35a:stats";
const HISTORY_KEY = "vs:pr35a:history";
const HISTORY_MAX = 8;

const DEFAULT_CONFIG: PollLifecycleConfig = {
  countdownSec: 8,
  postVoteSec: 3,
  celebrationSec: 4,
  pointsOnCorrect: 250,
  confettiEnabled: true,
};

export function getLifecycleConfig(): PollLifecycleConfig {
  if (typeof window === "undefined") return DEFAULT_CONFIG;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONFIG;
    return { ...DEFAULT_CONFIG, ...(JSON.parse(raw) as Partial<PollLifecycleConfig>) };
  } catch {
    return DEFAULT_CONFIG;
  }
}

export function setLifecycleConfig(patch: Partial<PollLifecycleConfig>): PollLifecycleConfig {
  const next = { ...getLifecycleConfig(), ...patch };
  if (typeof window !== "undefined") {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
  }
  return next;
}

/** Normalize a question so equivalent phrasings dedupe to a single key. */
export function normalizeQuestion(q: string): string {
  return q
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[¿?¡!.,;:"'`´]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function dedupKey(matchId: string, pollType: string, question: string): string {
  return `${matchId}::${pollType}::${normalizeQuestion(question)}`;
}

function readDedup(): Record<string, number> {
  if (typeof window === "undefined") return {};
  try { return JSON.parse(window.localStorage.getItem(DEDUP_KEY) || "{}") as Record<string, number>; }
  catch { return {}; }
}
function writeDedup(d: Record<string, number>) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(DEDUP_KEY, JSON.stringify(d)); } catch { /* noop */ }
}

export function hasAnswered(matchId: string, pollType: string, question: string): boolean {
  return !!readDedup()[dedupKey(matchId, pollType, question)];
}

export function markAnswered(matchId: string, pollType: string, question: string): void {
  const d = readDedup();
  d[dedupKey(matchId, pollType, question)] = Date.now();
  writeDedup(d);
  void trackEvent("poll_answered", { match_id: matchId, poll_type: pollType });
}

export function shouldShowPoll(matchId: string, pollType: string, question: string): boolean {
  const blocked = hasAnswered(matchId, pollType, question);
  if (blocked) {
    void trackEvent("poll_deduplicated", { match_id: matchId, poll_type: pollType });
  }
  return !blocked;
}

export function clearMatchDedup(matchId: string): void {
  const d = readDedup();
  const next: Record<string, number> = {};
  for (const k of Object.keys(d)) if (!k.startsWith(`${matchId}::`)) next[k] = d[k];
  writeDedup(next);
}

export interface AccuracyStats {
  total: number;
  correct: number;
  points: number;
  accuracyPct: number;
}

export function getAccuracyStats(): AccuracyStats {
  const empty: AccuracyStats = { total: 0, correct: 0, points: 0, accuracyPct: 0 };
  if (typeof window === "undefined") return empty;
  try {
    const raw = window.localStorage.getItem(STATS_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<AccuracyStats>;
    return { ...empty, ...parsed };
  } catch { return empty; }
}

export function recordPredictionResult(opts: { correct: boolean; points?: number }): AccuracyStats {
  const cur = getAccuracyStats();
  const total = cur.total + 1;
  const correct = cur.correct + (opts.correct ? 1 : 0);
  const pts = cur.points + (opts.correct ? (opts.points ?? getLifecycleConfig().pointsOnCorrect) : 0);
  const next: AccuracyStats = {
    total,
    correct,
    points: pts,
    accuracyPct: total ? Math.round((correct / total) * 100) : 0,
  };
  if (typeof window !== "undefined") {
    try { window.localStorage.setItem(STATS_KEY, JSON.stringify(next)); } catch { /* noop */ }
  }
  void trackEvent(opts.correct ? "prediction_correct" : "prediction_incorrect", {
    points: opts.correct ? (opts.points ?? getLifecycleConfig().pointsOnCorrect) : 0,
  });
  void trackEvent("accuracy_updated", { accuracy_pct: next.accuracyPct });
  if (opts.correct) void trackEvent("points_awarded", { points: opts.points ?? getLifecycleConfig().pointsOnCorrect });
  return next;
}

export interface PredictionHistoryEntry {
  id: string;
  label: string;
  correct: boolean;
  ts: number;
}

export function getPredictionHistory(): PredictionHistoryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PredictionHistoryEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

export function appendPredictionHistory(entry: Omit<PredictionHistoryEntry, "ts">): PredictionHistoryEntry[] {
  const list = getPredictionHistory();
  const next = [{ ...entry, ts: Date.now() }, ...list].slice(0, HISTORY_MAX);
  if (typeof window !== "undefined") {
    try { window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next)); } catch { /* noop */ }
  }
  return next;
}

export const PR35A_QA_ITEMS: ReadonlyArray<{ id: string; label: string }> = [
  { id: "auto-dismiss", label: "Encuestas y predicciones cierran al agotar countdown." },
  { id: "post-vote", label: "Tras votar desaparecen entre 2 y 4 segundos." },
  { id: "confirm", label: "Mini confirmación post-voto compacta y no intrusiva." },
  { id: "celebrate", label: "Celebración cuando el usuario acierta." },
  { id: "points", label: "Se otorgan puntos al acertar." },
  { id: "dedup", label: "No se repiten preguntas de respuesta única por partido." },
  { id: "persist", label: "Persistencia por partido (localStorage)." },
  { id: "zero-overlap", label: "Zero Overlap respetado (chat/botones libres)." },
  { id: "demo", label: "MODO DEMO intacto." },
  { id: "tsc", label: "tsc --noEmit = 0 errores." },
];